/**
 * Probe's sidebar, a record of rehearsals: starting one is the filled
 * button at the top, Home and Briefing are nav rows under it, and the
 * interviews follow in one list, each led by the debrief's rating glyph,
 * named by its review's title and closed by its day, the call that is up
 * pinned first.
 *
 * @module components/apps/probe/sidebar/ProbeSidebar
 */
import { Link } from "@tanstack/react-router";
import { FileTextIcon, HouseIcon, PlusIcon } from "lucide-react";

import { cn } from "~/lib/utils";
import type { AppAreaNavProps } from "../../AppsSidebar";
import { AppAreaNavGroup, AppAreaNavRow } from "../../appAreaNavParts";
import {
  AllInterviewsRow,
  InterviewList,
  InterviewListPlaceholder,
  InterviewRow,
  LiveRow,
  useProbeSidebar,
} from "./ProbeSidebarParts";


export function ProbeSidebar(props: AppAreaNavProps) {
  const { orgSegment, onNavigate } = props;
  const { section, allInterviews, view, status, reload, weak, live } = useProbeSidebar(props);

  return (
    <>
      <Link
        to="/$org/apps/probe"
        params={{ org: orgSegment }}
        onClick={onNavigate}
        // One call at a time: while one is up, it is the row pinned below.
        aria-disabled={live || undefined}
        tabIndex={live ? -1 : undefined}
        data-app-area-row="new-interview"
        className={cn(
          "flex h-9 items-center justify-center gap-2 rounded-lg px-3 text-sm font-medium outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar",
          live
            ? "pointer-events-none border border-sidebar-border text-sidebar-muted-foreground/60"
            : "bg-primary text-primary-foreground shadow-xs transition-[filter] hover:brightness-110",
        )}
      >
        <PlusIcon aria-hidden className="size-4" />
        New interview
      </Link>

      <AppAreaNavGroup>
        <AppAreaNavRow
          data-app-area-row="home"
          active={section?.kind === "practice" && !allInterviews}
          icon={<HouseIcon />}
          label="Home"
          count={weak}
          render={<Link to="/$org/apps/probe/practice" params={{ org: orgSegment }} onClick={onNavigate} />}
        />
        <AppAreaNavRow
          data-app-area-row="briefing"
          active={section?.kind === "briefing"}
          icon={<FileTextIcon />}
          label="Briefing"
          render={<Link to="/$org/apps/probe/briefing" params={{ org: orgSegment }} onClick={onNavigate} />}
        />
      </AppAreaNavGroup>

      <InterviewList label="Interviews">
        {view.live !== null ? (
          <LiveRow live={view.live} orgSegment={orgSegment} onNavigate={onNavigate} />
        ) : null}
        {status === "ready"
          ? view.interviews.map((interview) => (
              <InterviewRow
                key={interview.id}
                interview={interview}
                orgSegment={orgSegment}
                onNavigate={onNavigate}
              />
            ))
          : null}
        <InterviewListPlaceholder
          status={status}
          empty={view.total === 0 && view.live === null}
          reload={reload}
        />
        {status === "ready" && view.total > 0 ? (
          <AllInterviewsRow
            total={view.total}
            active={allInterviews}
            orgSegment={orgSegment}
            onNavigate={onNavigate}
          />
        ) : null}
      </InterviewList>
    </>
  );
}
